import type { DesignElement, ElementCategory } from '@canvas/shared-types';
import { DIAGRAM_CATEGORIES, DIAGRAM_CATALOG } from '@/data/diagramCatalog';
import { SHAPE_PREVIEW_SVGS } from '@/data/elementPreviewSvgs';

export const ELEMENT_CATEGORIES: ElementCategory[] = [
  { id: 'shapes', label: 'Shapes', kinds: ['shape', 'line'], order: 1 },
  { id: 'text', label: 'Text styles', kinds: ['text'], order: 2 },
  { id: 'frames', label: 'Frames', kinds: ['frame'], order: 3 },
  { id: 'graphics', label: 'Graphics', kinds: ['icon', 'illustration'], order: 4 },
  ...DIAGRAM_CATEGORIES,
];

export const ELEMENT_CATALOG: DesignElement[] = [
  // Shapes
  {
    elementId: 'shape-rect',
    name: 'Rectangle',
    kind: 'shape',
    category: 'shapes',
    tags: ['basic', 'box'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'image', value: SHAPE_PREVIEW_SVGS.rect, label: 'Rect' },
    defaultWidth: 240,
    defaultHeight: 160,
    factory: { type: 'shape', shape: 'rect', fill: '#0d7377' },
  },
  {
    elementId: 'shape-rounded',
    name: 'Rounded rectangle',
    kind: 'shape',
    category: 'shapes',
    tags: ['basic', 'card'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS.roundedRect,
      label: 'Round',
    },
    defaultWidth: 260,
    defaultHeight: 160,
    factory: { type: 'shape', shape: 'roundedRect', fill: '#1b4d89' },
  },
  {
    elementId: 'shape-circle',
    name: 'Circle',
    kind: 'shape',
    category: 'shapes',
    tags: ['basic', 'ellipse'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS.circle,
      label: 'Circle',
    },
    defaultWidth: 180,
    defaultHeight: 180,
    factory: { type: 'shape', shape: 'circle', fill: '#b54708' },
  },
  {
    elementId: 'shape-triangle',
    name: 'Triangle',
    kind: 'shape',
    category: 'shapes',
    tags: ['basic'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS.triangle,
      label: 'Tri',
    },
    defaultWidth: 180,
    defaultHeight: 160,
    factory: { type: 'shape', shape: 'triangle', fill: '#45566e' },
  },
  {
    elementId: 'shape-line',
    name: 'Line',
    kind: 'line',
    category: 'shapes',
    tags: ['divider', 'underline'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'image', value: SHAPE_PREVIEW_SVGS.line, label: 'Line' },
    defaultWidth: 280,
    defaultHeight: 4,
    factory: { type: 'shape', shape: 'line', stroke: '#152033', strokeWidth: 4 },
  },
  {
    elementId: 'shape-star',
    name: 'Star',
    kind: 'shape',
    category: 'shapes',
    tags: ['accent', 'badge'],
    audience: 'student',
    license: 'free',
    preview: { mode: 'image', value: SHAPE_PREVIEW_SVGS.star, label: 'Star' },
    defaultWidth: 160,
    defaultHeight: 160,
    factory: { type: 'shape', shape: 'star', fill: '#c9a227' },
  },
  {
    elementId: 'shape-hexagon',
    name: 'Hexagon',
    kind: 'shape',
    category: 'shapes',
    tags: ['polygon', 'basic'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS.hexagon,
      label: 'Hex',
    },
    defaultWidth: 170,
    defaultHeight: 170,
    factory: { type: 'shape', shape: 'polygon', sides: 6, fill: '#0d7377' },
  },
  {
    elementId: 'shape-octagon',
    name: 'Octagon',
    kind: 'shape',
    category: 'shapes',
    tags: ['polygon', 'basic'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS.octagon,
      label: 'Oct',
    },
    defaultWidth: 170,
    defaultHeight: 170,
    factory: { type: 'shape', shape: 'polygon', sides: 8, fill: '#5b6abf' },
  },

  // Text styles
  {
    elementId: 'text-heading',
    name: 'Heading',
    kind: 'text',
    category: 'text',
    tags: ['title', 'display'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'glyph', value: 'Aa', label: 'Heading' },
    defaultWidth: 400,
    defaultHeight: 60,
    factory: {
      type: 'text',
      text: 'Add a heading',
      fontSize: 48,
      fontWeight: 700,
      fontFamily: 'Outfit, Arial, sans-serif',
      fill: '#152033',
    },
  },
  {
    elementId: 'text-subheading',
    name: 'Subheading',
    kind: 'text',
    category: 'text',
    tags: ['subtitle'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'glyph', value: 'Bb', label: 'Subhead' },
    defaultWidth: 360,
    defaultHeight: 40,
    factory: {
      type: 'text',
      text: 'Add a subheading',
      fontSize: 28,
      fontWeight: 600,
      fontFamily: 'Outfit, Arial, sans-serif',
      fill: '#45566e',
    },
  },
  {
    elementId: 'text-body',
    name: 'Body text',
    kind: 'text',
    category: 'text',
    tags: ['paragraph'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'glyph', value: 'abc', label: 'Body' },
    defaultWidth: 420,
    defaultHeight: 80,
    factory: {
      type: 'text',
      text: 'Body copy for assignments, captions, or brief descriptions.',
      fontSize: 18,
      fontWeight: 400,
      fontFamily: 'Source Sans 3, Arial, sans-serif',
      fill: '#152033',
    },
  },
  {
    elementId: 'text-quote',
    name: 'Quote',
    kind: 'text',
    category: 'text',
    tags: ['pullquote', 'academic'],
    audience: 'student',
    license: 'free',
    preview: { mode: 'glyph', value: '“”', label: 'Quote' },
    defaultWidth: 380,
    defaultHeight: 70,
    factory: {
      type: 'text',
      text: '“A short quote or key takeaway.”',
      fontSize: 22,
      fontWeight: 500,
      fontFamily: 'Source Sans 3, Georgia, serif',
      fill: '#1b4d89',
    },
  },
  {
    elementId: 'text-label',
    name: 'Label / badge',
    kind: 'text',
    category: 'text',
    tags: ['tag', 'eyebrow'],
    audience: 'professional',
    license: 'free',
    preview: { mode: 'glyph', value: 'TAG', label: 'Label' },
    defaultWidth: 200,
    defaultHeight: 28,
    factory: {
      type: 'text',
      text: 'CASE STUDY',
      fontSize: 14,
      fontWeight: 700,
      fontFamily: 'Outfit, Arial, sans-serif',
      fill: '#0d7377',
    },
  },

  // Frames (placeholders — circle / rounded photo frames)
  {
    elementId: 'frame-circle',
    name: 'Circle frame',
    kind: 'frame',
    category: 'frames',
    tags: ['photo', 'avatar'],
    audience: 'all',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS['frame-circle'],
      label: '○ Frame',
    },
    defaultWidth: 200,
    defaultHeight: 200,
    factory: { type: 'shape', shape: 'circle', fill: '#e3e9f0', stroke: '#7b8aa0', strokeWidth: 2 },
  },
  {
    elementId: 'frame-rounded',
    name: 'Photo card frame',
    kind: 'frame',
    category: 'frames',
    tags: ['photo', 'card'],
    audience: 'professional',
    license: 'free',
    preview: {
      mode: 'image',
      value: SHAPE_PREVIEW_SVGS['frame-rounded'],
      label: 'Card',
    },
    defaultWidth: 280,
    defaultHeight: 200,
    factory: {
      type: 'shape',
      shape: 'roundedRect',
      fill: '#eef2f6',
      stroke: '#d0dae6',
      strokeWidth: 2,
    },
  },
  ...DIAGRAM_CATALOG,
];

export function getElementsByCategory(categoryId: string): DesignElement[] {
  return ELEMENT_CATALOG.filter((el) => el.category === categoryId);
}
